"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { Quote, Sparkles, CheckCircle2, ArrowRight, Award, ShieldCheck, Star } from "lucide-react";
import { ProcedureSectionData } from "@/lib/procedure-sections/types";
import { publicUrl } from "@/lib/content/path";

export function QuoteHighlightSection({ section }: { section: ProcedureSectionData; index: number }) {
  const variant = section.layoutVariant || "quote_doctor_portrait";

  // 1. DOCTOR PORTRAIT MASTERCLASS CARD
  if (variant === "quote_doctor_portrait") {
    return (
      <div className="w-full">
        <div className="relative overflow-hidden rounded-3xl border border-brand-orange/30 bg-gradient-to-br from-[#FFF9F5] via-white to-brand-peach/20 p-8 sm:p-14 shadow-xl">
          <Quote className="absolute right-6 top-6 h-28 w-28 text-brand-orange/10 pointer-events-none" />

          <div className="relative z-10 flex flex-col md:flex-row items-center gap-6 md:gap-10">
            {section.image ? (
              <div className="relative h-32 w-32 sm:h-40 sm:w-40 shrink-0 overflow-hidden rounded-3xl border-3 border-brand-orange shadow-2xl ring-4 ring-brand-orange/15">
                <Image
                  src={publicUrl(section.image)}
                  alt={section.imageAlt || "Doctor highlight"}
                  fill
                  unoptimized
                  className="object-cover"
                />
              </div>
            ) : (
              <div className="flex h-28 w-28 shrink-0 items-center justify-center rounded-3xl bg-gradient-to-br from-brand-orange to-brand-orange-deep text-white shadow-2xl shadow-brand-orange/30">
                <Quote className="h-12 w-12" />
              </div>
            )}

            <div className="flex-1 text-center md:text-left">
              {section.badge && (
                <div className="inline-flex items-center gap-2 rounded-full bg-brand-orange/10 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-brand-orange-deep mb-3">
                  <Award className="h-3.5 w-3.5 text-brand-orange" />
                  <span>{section.badge}</span>
                </div>
              )}
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-brand-black leading-snug">
                &ldquo;{section.title}&rdquo;
              </h3>
              {section.contentHtml && (
                <div 
                  className="mt-4 text-base sm:text-lg text-brand-gray italic leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: section.contentHtml }}
                />
              )}
              <div className="mt-5 flex items-center justify-center md:justify-start gap-3">
                <div className="h-0.5 w-8 bg-brand-orange" />
                <p className="text-sm font-bold uppercase tracking-wider text-brand-orange-deep">
                  {section.subtitle || "Dr. Gaurav Garg — Founder & Chief Surgeon (AIIMS & ISHRS)"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 2. CENTERED LUXURY EDITORIAL QUOTE
  if (variant === "quote_centered_luxury") {
    return (
      <div className="w-full max-w-4xl mx-auto text-center">
        <div className="relative rounded-3xl border border-brand-gray/20 bg-white p-10 sm:p-16 shadow-xl">
          <div className="flex justify-center mb-6">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-peach/40 text-brand-orange shadow-inner">
              <Quote className="h-8 w-8" />
            </div>
          </div>
          <div className="flex items-center justify-center gap-1.5 text-amber-500 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-amber-500" />
            ))}
          </div>
          {section.badge && (
            <span className="font-accent text-xs font-bold uppercase tracking-widest text-brand-orange-deep">
              {section.badge}
            </span>
          )}
          <h3 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-brand-black leading-relaxed">
            &ldquo;{section.title}&rdquo;
          </h3>
          {section.contentHtml && (
            <div 
              className="mt-6 text-base sm:text-lg text-brand-gray leading-relaxed max-w-2xl mx-auto"
              dangerouslySetInnerHTML={{ __html: section.contentHtml }}
            />
          )}
          <p className="mt-6 text-xs font-bold uppercase tracking-wider text-brand-orange-deep">
            {section.subtitle || "— Certified Clinical Insight"}
          </p>
        </div>
      </div>
    );
  }

  // 3. HIGH-IMPACT BRAND GRADIENT BANNER + CTA
  if (variant === "quote_gradient_banner") {
    return (
      <div className="w-full">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-brand-orange via-brand-orange-deep to-[#C95500] p-8 sm:p-14 text-white shadow-2xl">
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl text-center md:text-left">
              {section.badge && (
                <span className="inline-block rounded-full bg-white/20 backdrop-blur-md px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-white">
                  {section.badge}
                </span>
              )}
              <h3 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight text-white">
                &ldquo;{section.title}&rdquo;
              </h3>
              {section.contentHtml && (
                <div 
                  className="mt-3 text-white/90 text-base sm:text-lg leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: section.contentHtml }}
                />
              )}
              <p className="mt-4 text-xs font-bold uppercase tracking-widest text-white/80">
                {section.subtitle || "— Dr. Gaurav Garg, MD Dermatology"}
              </p>
            </div>
            <Link
              href="/appointment"
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-bold text-brand-orange shadow-xl hover:bg-[#FAF9F6] transition-all hover:scale-105"
            >
              <span>Book Consultation</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // 4. DUAL-BADGE TRUST CERTIFICATE CARD
  if (variant === "quote_dual_badge_card") {
    return (
      <div className="w-full max-w-4xl mx-auto">
        <div className="relative rounded-3xl border-2 border-brand-orange/40 bg-gradient-to-br from-[#FFF8F3] via-white to-brand-peach/15 p-8 sm:p-12 shadow-xl">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 border-b border-brand-orange/20 pb-6 mb-6">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-8 w-8 text-brand-orange" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-brand-orange-deep">Certified Excellence</p>
                <p className="text-base font-bold text-brand-black">AIIMS &amp; ISHRS Endorsed</p>
              </div>
            </div>
            {section.badge && (
              <span className="rounded-full bg-brand-orange text-white px-3.5 py-1 text-xs font-bold uppercase tracking-wider">
                {section.badge}
              </span>
            )}
          </div>

          <h3 className="text-2xl sm:text-3xl font-bold text-brand-black leading-snug">
            &ldquo;{section.title}&rdquo;
          </h3>
          {section.contentHtml && (
            <div 
              className="mt-4 text-base sm:text-lg text-brand-gray leading-relaxed"
              dangerouslySetInnerHTML={{ __html: section.contentHtml }}
            />
          )}
          <p className="mt-5 text-sm font-bold uppercase text-brand-orange-deep">
            {section.subtitle || "— Dr. Gaurav Garg (Founder, Dermalife)"}
          </p>
        </div>
      </div>
    );
  }

  // 5. CLINICAL INSIGHT & METRIC PILL PAIR
  if (variant === "quote_split_stat") {
    return (
      <div className="w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8 rounded-3xl border border-brand-gray/20 bg-white p-8 sm:p-10 shadow-lg">
            <Quote className="h-8 w-8 text-brand-orange mb-3" />
            <h3 className="text-2xl sm:text-3xl font-bold text-brand-black leading-snug">
              &ldquo;{section.title}&rdquo;
            </h3>
            {section.contentHtml && (
              <div 
                className="mt-3 text-base text-brand-gray leading-relaxed"
                dangerouslySetInnerHTML={{ __html: section.contentHtml }}
              />
            )}
            <p className="mt-4 text-xs font-bold uppercase text-brand-orange-deep">
              {section.subtitle || "— Clinical Leadership"}
            </p>
          </div>

          <div className="lg:col-span-4 space-y-4">
            <div className="rounded-3xl bg-gradient-to-br from-brand-peach/40 to-brand-peach/15 border border-brand-orange/30 p-6 text-center shadow-md">
              <p className="text-3xl sm:text-4xl font-black text-brand-orange-deep">100%</p>
              <p className="mt-1 text-xs font-bold uppercase tracking-wider text-brand-black">Natural Direction &amp; Density</p>
            </div>
            <div className="rounded-3xl bg-white border border-brand-gray/20 p-6 text-center shadow-md">
              <p className="text-3xl sm:text-4xl font-black text-brand-black">0%</p>
              <p className="mt-1 text-xs font-bold uppercase tracking-wider text-brand-gray">Linear Scarring Guarantee</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 6. MINIMAL FLOATING PILL CALLOUT
  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="relative rounded-3xl border-l-4 border-brand-orange border-y border-r border-brand-gray/15 bg-white p-8 sm:p-10 shadow-md">
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-peach/40 text-brand-orange mt-1">
            <Quote className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-brand-black leading-snug">
              &ldquo;{section.title}&rdquo;
            </h3>
            {section.contentHtml && (
              <div 
                className="mt-3 text-sm sm:text-base text-brand-gray leading-relaxed"
                dangerouslySetInnerHTML={{ __html: section.contentHtml }}
              />
            )}
            <p className="mt-3 text-xs font-bold uppercase text-brand-orange-deep">
              {section.subtitle || "— Clinical Takeaway"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
